import { type DateFormat, type TimeFormat } from '@databases/model/field';
import { type ObjectMode } from '@databases/types';
import {
  type ContentReference,
  type TemplateReference,
} from '@databases/types/confluence';
import {
  type BacklinkDisplayType,
  type Database,
  type DatabaseCommentThread,
  type DatabaseEntry,
  type DatabaseField,
  type DatabaseFieldSettings,
  DatabaseFieldType,
  type DatabaseValue,
  DEFAULT_ENTRY_ID,
  type DisplayedDetailType,
  type MediaFormat,
  type MinimumOrMaximumFractionDigitsType,
  type NumberFormat,
  type NumberSeparators,
  type PageSubType,
  type ParentPageOption,
  type ParentPageType,
  type PrefixOrSuffixFieldType,
  type RawDatabaseValue,
  type SelectOption,
  type SeparatorFieldType,
} from '@databases/types/database';
import {
  type DatabaseCalculationType,
  type DatabaseCardWidth,
  type DatabaseCoverFieldType,
  type DatabaseFilter,
  type DatabaseFilterUsageType,
  type DatabaseGroupSortType,
  type DatabaseLayoutType,
  type DatabaseView,
} from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { produce } from 'immer';
import { clone, cloneDeep } from 'lodash-es';
import { type Op } from 'quill-delta';

import { type ADFDocument } from '@canvas/adf-types';

import { getUniqueFieldName } from '../field-name';

import { getInitialValue } from './field-types';
import {
  createEntry,
  createField,
  getDatabaseEntries,
  getDatabaseEntryRequired,
  getDatabaseFieldOptional,
  getDatabaseFieldRequired,
  getExistingFieldNames,
} from './utils';
import {
  applyViewChange,
  getNewIndexWithHiddenFields,
  getViewById,
  ViewChangeTypes,
  ViewFilterChangeType,
} from './view/view';

export enum MultiValueChangeType {
  SET_TO,
  EDIT,
  APPEND,
  REMOVE,
  REORDER,
}

export type MultiValueFieldType =
  | DatabaseFieldType.ENTRY_LINK
  | DatabaseFieldType.SELECT
  | DatabaseFieldType.PAGE_LINK
  | DatabaseFieldType.JIRA_ISSUE
  | DatabaseFieldType.USER
  | DatabaseFieldType.ATTACHMENT;

export type MultiChange<T extends MultiValueFieldType, U = object> =
  | {
      type: MultiValueChangeType.EDIT;
      value: RawDatabaseValue<T, U>[0];
      index: number;
    }
  | {
      type: MultiValueChangeType.APPEND;
      value: RawDatabaseValue<T, U>[0];
    }
  | {
      type: MultiValueChangeType.REMOVE;
      index: number;
    }
  | {
      type: MultiValueChangeType.REORDER;
      currentIndex: number;
      newIndex: number;
    }
  | {
      type: MultiValueChangeType.SET_TO;
      value: RawDatabaseValue<T, U>;
    };

export type HyperlinkChange =
  | {
      type: MultiValueChangeType.EDIT;
      delta: Op[];
      index: number;
    }
  | {
      type: MultiValueChangeType.APPEND;
      value: RawDatabaseValue<DatabaseFieldType.HYPERLINK>[0];
    }
  | {
      type: MultiValueChangeType.REMOVE;
      index: number;
    }
  | {
      type: MultiValueChangeType.REORDER;
      currentIndex: number;
      newIndex: number;
    }
  | {
      type: MultiValueChangeType.SET_TO;
      value: RawDatabaseValue<DatabaseFieldType.HYPERLINK>[0][];
    };

export type ValueChange =
  | {
      type: DatabaseFieldType.TEXT;
      delta: Op[];
    }
  | {
      type: DatabaseFieldType.TEXT;
      adf: ADFDocument;
    }
  | {
      type: DatabaseFieldType.TEXT;
      delta: Op[];
      adf: ADFDocument;
    }
  | {
      type: DatabaseFieldType.ENTRY_LINK;
      delta: MultiChange<DatabaseFieldType.ENTRY_LINK>;
    }
  | {
      type: DatabaseFieldType.SELECT;
      delta: MultiChange<DatabaseFieldType.SELECT>;
    }
  | {
      type: DatabaseFieldType.PAGE_LINK;
      delta: MultiChange<
        DatabaseFieldType.PAGE_LINK,
        { contentId: string; ari: string | undefined }
      >;
    }
  | {
      type: DatabaseFieldType.DATE;
      value: RawDatabaseValue<DatabaseFieldType.DATE>;
    }
  | {
      type: DatabaseFieldType.NUMBER;
      value: RawDatabaseValue<DatabaseFieldType.NUMBER>;
    }
  | {
      type: DatabaseFieldType.ATTACHMENT;
      delta: MultiChange<DatabaseFieldType.ATTACHMENT>;
    }
  | {
      type: DatabaseFieldType.CHECKBOX;
      value: RawDatabaseValue<DatabaseFieldType.CHECKBOX>;
    }
  | {
      type: DatabaseFieldType.HYPERLINK;
      delta: HyperlinkChange;
    }
  | {
      type: DatabaseFieldType.USER;
      delta: MultiChange<DatabaseFieldType.USER>;
    }
  | {
      type: DatabaseFieldType.JIRA_ISSUE;
      delta: MultiChange<DatabaseFieldType.JIRA_ISSUE>;
    };

export type SelectOptionsChange =
  | {
      type: MultiValueChangeType.APPEND;
      value: SelectOption;
    }
  | {
      type: MultiValueChangeType.REMOVE;
      index: number;
    }
  | {
      type: MultiValueChangeType.REORDER;
      currentIndex: number;
      newIndex: number;
    }
  | {
      type: 'set_color';
      index: number;
      color: SelectOption['color'];
    }
  | {
      type: 'update_label';
      index: number;
      delta: Op[];
    };

export type DatabaseChange =
  | {
      type: DatabaseChangeType.ADD_ENTRY;
      entry: DatabaseEntry;
      index?: number;
    }
  | {
      type: DatabaseChangeType.ADD_FIELD;
      field: DatabaseField;
      currentViewId?: string;
      index?: number;
    }
  | {
      type: DatabaseChangeType.UPDATE_FIELD;
      /** The updated field. The field is identified by its ID. */
      field: DatabaseField;
    }
  | {
      type: DatabaseChangeType.RESET_FIELD;
      fieldId: string;
    }
  | {
      type: DatabaseChangeType.SET_FIELD_SETTINGS;
      fieldId: string;
      settings: Partial<
        DatabaseFieldSettings & {
          parentPage: {
            contentId: string;
          };
        }
      >;
    }
  | {
      type: DatabaseChangeType.SET_FIELD_SETTINGS_NUMBER_FORMAT;
      fieldId: string;
      newValue: NumberFormat;
    }
  | {
      type: DatabaseChangeType.SET_FIELD_SETTINGS_MEDIA_FORMAT;
      fieldId: string;
      mediaFormat: MediaFormat;
    }
  | {
      type: DatabaseChangeType.SET_FIELD_SETTINGS_MINIMUM_OR_MAXIMUM_FRACTION_DIGITS_FIELD;
      fieldId: string;
      changeType: MinimumOrMaximumFractionDigitsType;
      newValue?: number;
    }
  | {
      type: DatabaseChangeType.SET_FIELD_SETTINGS_SEPARATOR_FIELD;
      fieldId: string;
      changeType: SeparatorFieldType;
      newValue?: NumberSeparators;
    }
  | {
      type: DatabaseChangeType.SET_FIELD_SETTINGS_THOUSANDS_AND_DECIMAL_FIELD;
      fieldId: string;
      newValue: {
        thousandsSeparator?: NumberSeparators;
        decimalSeparator: NumberSeparators;
      };
    }
  | {
      type: DatabaseChangeType.SET_FIELD_SETTINGS_PREFIX_OR_SUFFIX_FIELD;
      fieldId: string;
      changeType: PrefixOrSuffixFieldType;
      newValue?: string;
    }
  | {
      type: DatabaseChangeType.SET_IS_CREATABLE;
      fieldId: string;
      isCreatable: boolean;
    }
  | {
      type: DatabaseChangeType.SET_IS_MULTI;
      fieldId: string;
      isMulti: boolean;
    }
  | {
      type: DatabaseChangeType.SET_ALLOW_EDIT;
      fieldId: string;
      allowEdit: boolean;
    }
  | {
      type: DatabaseChangeType.SET_DISPLAY_DETAIL;
      fieldId: string;
      displayedDetailTypes: DisplayedDetailType;
    }
  | {
      type: DatabaseChangeType.SET_CONTENT_FIELD;
      fieldId: string;
      contentField?: string;
    }
  | {
      type: DatabaseChangeType.SET_BACKLINK_DISPLAY_TYPE;
      fieldId: string;
      backlinkDisplayType: BacklinkDisplayType;
    }
  | {
      type: DatabaseChangeType.SET_DATABASE_ID;
      fieldId: string;
      databaseId?: string;
      contentId?: string;
      databaseSpace?: { key: string };
    }
  | {
      type: DatabaseChangeType.SET_INCLUDE_VERSION_COMMENT;
      fieldId: string;
      includeVersionComment: boolean;
    }
  | {
      type: DatabaseChangeType.SET_JIRA_FIELD;
      fieldId: string;
      jiraField?: string;
    }
  | {
      type: DatabaseChangeType.SET_JIRA_SERVER_ID;
      fieldId: string;
      jiraServerId?: string;
    }
  | {
      type: DatabaseChangeType.SET_CLOUD_ID;
      fieldId: string;
      cloudId?: string;
    }
  | {
      type: DatabaseChangeType.SET_RESOLVE;
      fieldId: string;
      resolve: boolean;
    }
  | {
      type: DatabaseChangeType.SET_ENTRY_FIELD_FIELD;
      fieldId: string;
      entryFieldField?: string;
    }
  | {
      type: DatabaseChangeType.SET_PAGE_SUB_TYPE;
      fieldId: string;
      pageSubType: PageSubType;
    }
  | {
      type: DatabaseChangeType.SET_PARENT_PAGE_OPTION;
      fieldId: string;
      parentPageOption: ParentPageOption;
    }
  | {
      type: DatabaseChangeType.SET_IS_PARENT_PAGE_SOURCE_DATABASE;
      fieldId: string;
      isParentPageSourceDatabase?: boolean;
    }
  | {
      type: DatabaseChangeType.SET_PAGE_LOCATION;
      fieldId: string;
      newParentPageType: ParentPageType;
    }
  | {
      type: DatabaseChangeType.SET_PARENT_PAGE;
      fieldId: string;
      newParentPage: ContentReference | undefined;
    }
  | {
      type: DatabaseChangeType.SET_PAGE_TEMPLATE;
      fieldId: string;
      newPageTemplate: TemplateReference;
    }
  | {
      type: DatabaseChangeType.SET_DISPLAY_ENTRY_ON_PAGE;
      fieldId: string;
      displayEntryOnPage: boolean;
    }
  | {
      type: DatabaseChangeType.SET_FIELD_ID;
      fieldId: string;
      settingsFieldId?: string;
    }
  | {
      type: DatabaseChangeType.SET_SELECT_OPTIONS;
      fieldId: string;
      newValue: SelectOption[];
      change: SelectOptionsChange;
    }
  | {
      type: DatabaseChangeType.SET_FIELD_NAME;
      fieldId: string;
      name: string;
      delta: Op[];
    }
  | {
      fieldId: string;
      type: DatabaseChangeType.RESET_FIELD_NAME;
    }
  | {
      type: DatabaseChangeType.SET_FIELD_DESCRIPTION;
      fieldId: string;
      description: string;
      delta: Op[];
    }
  | {
      type: DatabaseChangeType.SET_SHOW_FIELD_DESCRIPTION;
      fieldId: string;
      showDescription: boolean;
    }
  | {
      type: DatabaseChangeType.SET_FIELD_TYPE;
      fieldId: string;
      fieldType: DatabaseFieldType;
    }
  | {
      type: DatabaseChangeType.SET_FIELD_WIDTH;
      fieldId: string;
      width?: number;
    }
  | {
      type: DatabaseChangeType.REORDER_FIELD;
      fieldId: string;
      newIndex: number;
      hiddenFields: string[];
    }
  | {
      type: DatabaseChangeType.CLONE_FIELDS;
      /** A map from old field ID to new field ID (of the cloned field). */
      fieldIds: Record<string, string>;
      currentViewId: string;
    }
  | {
      type: DatabaseChangeType.DELETE_FIELD;
      fieldId: string;
    }
  | {
      type: DatabaseChangeType.UNDELETE_FIELD;
      fieldId: string;
    }
  | {
      type: DatabaseChangeType.SET_VALUE;
      entryId: string;
      fieldId: string;
      value: DatabaseValue<DatabaseFieldType, { contentId: string }>;
      change: ValueChange | null;
    }
  | {
      type: DatabaseChangeType.CREATE_FIELD_AND_SET_VALUE;
      field: DatabaseField;
      currentViewId?: string;
      index?: number;
      entryId: string;
      value: DatabaseValue<DatabaseFieldType, { contentId: string }>;
      change: ValueChange | null;
    }
  | {
      type: DatabaseChangeType.BULK_SET_UNDEFINED_VALUES;
      fieldId: string;
      value: DatabaseValue<DatabaseFieldType, { contentId: string }>;
      change: ValueChange | null;
    }
  | {
      type: DatabaseChangeType.CLEAR_ENTRIES;
      entryIds?: string[];
      fieldIds?: string[];
    }
  | {
      type: DatabaseChangeType.RESET_VALUE;
      entryId: string;
      fieldId: string;
    }
  | {
      type: DatabaseChangeType.REORDER_ENTRIES;
      entryIds: string[];
      newIndex: number;
    }
  | {
      type: DatabaseChangeType.CLONE_ENTRIES;
      /** A map from old entry ID to new entry ID (of the cloned entry). */
      entryIds: Record<string, string>;
    }
  | {
      type: DatabaseChangeType.DELETE_ENTRY;
      entryId: string;
      /** If true, delete the Confluence page referenced in the title field. */
      deletePage?: boolean;
    }
  | {
      type: DatabaseChangeType.UNDELETE_ENTRY;
      entryId: string;
    }
  | {
      type: DatabaseChangeType.ADD_SAVED_VIEW;
      view: DatabaseView;
    }
  | {
      type: DatabaseChangeType.SET_SAVED_VIEW;
      view: DatabaseView;
    }
  | {
      type: DatabaseChangeType.DELETE_SAVED_VIEW;
      viewId: string;
    }
  | {
      type: DatabaseChangeType.REORDER_SAVED_VIEW;
      viewId: string;
      newIndex: number;
    }
  | {
      type: DatabaseChangeType.SET_DEFAULT_SAVED_VIEW;
      viewId: string;
    }
  | {
      type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_TYPE;
      viewId: string;
      newLayoutType: DatabaseLayoutType;
    }
  | {
      type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_CARD_WIDTH;
      viewId: string;
      newCardWidth: DatabaseCardWidth;
    }
  | {
      type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_COVER_FIELD_ID;
      viewId: string;
      newCoverFieldId: DatabaseCoverFieldType | string;
    }
  | {
      type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_FIELD_NAMES;
      viewId: string;
      newShowFieldNames: boolean;
    }
  | {
      type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_DATE_FIELD_ID;
      viewId: string;
      newDateFieldId?: string;
    }
  | {
      type: DatabaseChangeType.SET_GROUPING_FIELD;
      viewId: string;
      fieldId: string;
      isSubgroup?: boolean;
      manualGroupOrder?: string[];
    }
  | {
      type: DatabaseChangeType.SET_GROUPING_SORT_TYPE;
      viewId: string;
      sortType: DatabaseGroupSortType;
      isSubgroup?: boolean;
    }
  | {
      type: DatabaseChangeType.SET_GROUPING_MANUAL_GROUP_ORDER;
      viewId: string;
      manualGroupOrder: string[];
      isSubgroup?: boolean;
    }
  | {
      type: DatabaseChangeType.CLEAR_GROUPING;
      viewId: string;
      isSubgroup?: boolean;
    }
  | {
      type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_GROUP_FIELD_ID;
      viewId: string;
      newGroupFieldId?: string;
    }
  | {
      type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_NUMBERED_ENTRIES;
      viewId: string;
      newShowNumberedEntries: boolean;
    }
  | {
      type: DatabaseChangeType.UPDATE_SAVED_VIEW_NAME;
      viewId: string;
      newName: string;
      delta: Op[];
    }
  | {
      type: DatabaseChangeType.UPDATE_SAVED_VIEW_FIELDS;
      viewId: string;
      fieldKey: string;
      fieldValue: boolean;
      existingFields?: DatabaseField[];
    }
  | {
      type: DatabaseChangeType.UPDATE_SAVED_VIEW_SORT;
      viewId: string;
      sort:
        | {
            fieldId: string;
            ascending: boolean;
          }
        | undefined;
    }
  | {
      type: DatabaseChangeType.UPDATE_SAVED_VIEW_SORT_VALUE;
      viewId: string;
      fieldId: string;
      ascending: boolean;
      customOrder?: boolean;
    }
  | {
      type: DatabaseChangeType.INSERT_SAVED_VIEW_SORT;
      viewId: string;
      sort: {
        fieldId: string;
        ascending: boolean;
      };
      index: number;
    }
  | {
      type: DatabaseChangeType.APPEND_SAVED_VIEW_SORT;
      viewId: string;
      sort: {
        fieldId: string;
        ascending: boolean;
      };
    }
  | {
      type: DatabaseChangeType.DELETE_SAVED_VIEW_SORT;
      viewId: string;
      fieldId: string;
    }
  | {
      type: DatabaseChangeType.REORDER_SAVED_VIEW_SORT;
      viewId: string;
      fieldId: string;
      newIndex: number;
    }
  | {
      type: DatabaseChangeType.CLEAR_SAVED_VIEW_SORT;
      viewId: string;
    }
  | {
      type:
        | DatabaseChangeType.SET_SAVED_VIEW_FILTER
        | DatabaseChangeType.SET_SAVED_VIEW_FILTER_FIELD_ID
        | DatabaseChangeType.SET_SAVED_VIEW_FILTER_TYPE;
      viewId: string;
      newFilter: DatabaseFilter;
      path?: number[];
    }
  | {
      type: DatabaseChangeType.SET_SAVED_VIEW_FILTER_USAGE_TYPE;
      viewId: string;
      usageType: DatabaseFilterUsageType;
      usageFilterId?: string;
      path?: number[];
    }
  | {
      type: DatabaseChangeType.SET_SAVED_VIEW_FILTER_VALUE;
      viewId: string;
      newFilter: DatabaseFilter;
      change: ValueChange | null;
      path?: number[];
    }
  | {
      type: DatabaseChangeType.APPEND_SAVED_VIEW_FILTER;
      viewId: string;
      filterGroupId?: string;
      filterGroupPath?: number[];
      newFilter: DatabaseFilter;
      path?: number[];
    }
  | {
      type: DatabaseChangeType.REMOVE_SAVED_VIEW_FILTER;
      viewId: string;
      filterId: string;
      path?: number[];
    }
  | {
      type: DatabaseChangeType.CLEAR_SAVED_VIEW_FILTER;
      viewId: string;
    }
  | {
      type: DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS;
      viewId: string;
      fieldId: string;
      value: DatabaseCalculationType;
    }
  | {
      type: DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION;
      viewId: string;
      fieldId: string;
    }
  | {
      type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES;
      changes: DatabaseChange[];
    }
  | {
      type: DatabaseChangeType.TOGGLE_LOCK_STRUCTURE;
      newValue?: boolean;
    }
  | {
      type: DatabaseChangeType.SET_DEFAULT_ENABLED;
      fieldId: string;
      defaultEnabled: boolean;
    }
  | {
      type: DatabaseChangeType.SET_MENTION_NOTIFICATION_ENABLED;
      fieldId: string;
      mentionNotificationEnabled: boolean;
    }
  | {
      type: DatabaseChangeType.ADD_VALUE_COMMENT_THREAD;
      fieldId: string;
      entryId: string;
      newThread: DatabaseCommentThread;
    }
  | {
      type: DatabaseChangeType.REMOVE_VALUE_COMMENT_THREAD;
      fieldId: string;
      entryId: string;
      threadId: string;
    }
  | {
      type: DatabaseChangeType.SET_VALUE_COMMENT_THREAD;
      fieldId: string;
      entryId: string;
      newThread: DatabaseCommentThread;
    }
  | {
      type: DatabaseChangeType.SET_VALUE_COMMENT_THREAD_RESOLVED;
      fieldId: string;
      entryId: string;
      threadId: string;
      isResolved: boolean;
    }
  | {
      type: DatabaseChangeType.SET_VALUE_COMMENT_THREAD_COMMENT_COUNT;
      fieldId: string;
      entryId: string;
      threadId: string;
      commentCount: number;
    }
  | {
      type: DatabaseChangeType.INCREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT;
      fieldId: string;
      entryId: string;
      threadId: string;
    }
  | {
      type: DatabaseChangeType.DECREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT;
      fieldId: string;
      entryId: string;
      threadId: string;
    }
  | {
      type: DatabaseChangeType.INCREMENT_VALUE_COMMENT_THREAD_VERSION_NUMBER;
      fieldId: string;
      entryId: string;
      threadId: string;
    }
  | {
      type: DatabaseChangeType.SET_DATE_FIELD_DATE_FORMAT;
      fieldId: string;
      dateFormat: DateFormat;
    }
  | {
      type: DatabaseChangeType.SET_DATE_FIELD_TIME_FORMAT;
      fieldId: string;
      timeFormat: TimeFormat | undefined;
    }
  | {
      type: DatabaseChangeType.SET_DATE_FIELD_ENABLE_DATE_RANGE;
      fieldId: string;
      enableDateRange: boolean;
    };

export type DatabaseChangeset = DatabaseChange[];

const DELETION_TYPES = [DatabaseChangeType.DELETE_FIELD, DatabaseChangeType.DELETE_ENTRY];

/**
 * Applies a single change to the given database and returns the updated database. The changes are not persisted,
 * an updated copy of the database is simply returned. The original database object is not touched.
 */
export function applyDatabaseChange<Mode extends ObjectMode.EXISTING | ObjectMode.UPDATE>(
  database: Database<Mode>,
  change: DatabaseChange
): Database<Mode> {
  return produce(database, (database: Database<Mode>) => {
    switch (change.type) {
      case DatabaseChangeType.ADD_ENTRY:
        database.entriesById[change.entry.id] = change.entry;
        if (change.index != null) {
          database.entries.splice(change.index, 0, change.entry.id);
        } else {
          database.entries.push(change.entry.id);
        }
        break;

      case DatabaseChangeType.ADD_FIELD:
        database.fieldsById[change.field.id] = change.field;
        if (change.index != null) {
          database.fields.splice(change.index, 0, change.field.id);
        } else {
          database.fields.push(change.field.id);
        }

        database.views.forEach((view) => {
          if (view.settings.fields) {
            view.settings.fields[change.field.id] = view.id === change.currentViewId;
          }
        });
        break;

      case DatabaseChangeType.UPDATE_FIELD:
        database.fieldsById[change.field.id] = change.field;
        break;

      case DatabaseChangeType.SET_IS_CREATABLE:
        database.fieldsById[change.fieldId].settings.creatable = change.isCreatable;
        break;

      case DatabaseChangeType.SET_IS_MULTI:
        database.fieldsById[change.fieldId].settings.multi = change.isMulti;
        break;

      case DatabaseChangeType.SET_ALLOW_EDIT:
        database.fieldsById[change.fieldId].settings.allowEdit = change.allowEdit;
        break;

      case DatabaseChangeType.SET_DISPLAY_DETAIL:
        database.fieldsById[change.fieldId].settings.displayedDetail =
          change.displayedDetailTypes;
        break;

      case DatabaseChangeType.SET_CONTENT_FIELD:
        database.fieldsById[change.fieldId].settings.contentField = change.contentField;
        break;

      case DatabaseChangeType.SET_BACKLINK_DISPLAY_TYPE:
        database.fieldsById[change.fieldId].settings.backlinkDisplayType =
          change.backlinkDisplayType;
        break;

      case DatabaseChangeType.SET_DATABASE_ID:
        database.fieldsById[change.fieldId].settings.databaseId = change.databaseId;
        database.fieldsById[change.fieldId].settings.contentId = change.contentId;
        database.fieldsById[change.fieldId].settings.databaseSpace = change.databaseSpace;
        database.defaultEntry.values[change.fieldId] = {
          type: DatabaseFieldType.ENTRY_LINK,
          value: [],
        };
        break;

      case DatabaseChangeType.SET_INCLUDE_VERSION_COMMENT:
        database.fieldsById[change.fieldId].settings.includeVersionComment =
          change.includeVersionComment;
        break;

      case DatabaseChangeType.SET_JIRA_FIELD:
        database.fieldsById[change.fieldId].settings.jiraField = change.jiraField;
        break;

      case DatabaseChangeType.SET_JIRA_SERVER_ID:
        database.fieldsById[change.fieldId].settings.jiraServerId = change.jiraServerId;
        break;

      case DatabaseChangeType.SET_CLOUD_ID:
        database.fieldsById[change.fieldId].settings.cloudId = change.cloudId;
        break;

      case DatabaseChangeType.SET_RESOLVE:
        database.fieldsById[change.fieldId].settings.resolve = change.resolve;
        break;

      case DatabaseChangeType.SET_ENTRY_FIELD_FIELD:
        database.fieldsById[change.fieldId].settings.lookupFieldId =
          change.entryFieldField;
        break;

      case DatabaseChangeType.SET_PAGE_SUB_TYPE:
        database.fieldsById[change.fieldId].settings.pageSubType = change.pageSubType;
        break;

      case DatabaseChangeType.SET_PARENT_PAGE_OPTION:
        database.fieldsById[change.fieldId].settings.parentPageOption =
          change.parentPageOption;
        break;

      case DatabaseChangeType.SET_IS_PARENT_PAGE_SOURCE_DATABASE:
        database.fieldsById[change.fieldId].settings.isParentPageSourceDatabase =
          change.isParentPageSourceDatabase;
        break;

      // Dead code until we set parentPageType properly
      case DatabaseChangeType.SET_PAGE_LOCATION:
        database.fieldsById[change.fieldId].settings.parentPageType =
          change.newParentPageType;
        break;

      case DatabaseChangeType.SET_PARENT_PAGE:
        database.fieldsById[change.fieldId].settings.parentPage = change.newParentPage;
        break;

      case DatabaseChangeType.SET_PAGE_TEMPLATE:
        database.fieldsById[change.fieldId].settings.pageTemplate =
          change.newPageTemplate;
        break;

      case DatabaseChangeType.SET_DISPLAY_ENTRY_ON_PAGE:
        database.fieldsById[change.fieldId].settings.displayEntryOnPage =
          change.displayEntryOnPage;
        break;

      case DatabaseChangeType.SET_FIELD_ID:
        database.fieldsById[change.fieldId].settings.fieldId = change.settingsFieldId;
        break;

      case DatabaseChangeType.SET_SELECT_OPTIONS:
        database.fieldsById[change.fieldId].settings.selectOptions = change.newValue;
        break;

      case DatabaseChangeType.RESET_FIELD:
        // Currently we only support this in addChangeToChangeset()
        break;

      case DatabaseChangeType.SET_FIELD_SETTINGS: {
        const field = getDatabaseFieldOptional(database, change.fieldId);
        if (field) {
          Object.assign(field.settings, change.settings);
        }
        break;
      }

      case DatabaseChangeType.SET_FIELD_SETTINGS_NUMBER_FORMAT: {
        const field = getDatabaseFieldOptional(database, change.fieldId);
        if (field) {
          Object.assign(field.settings, {
            ...field.settings,
            numberFormat: change.newValue,
          });
        }
        break;
      }

      case DatabaseChangeType.SET_FIELD_SETTINGS_MEDIA_FORMAT: {
        database.fieldsById[change.fieldId].settings.mediaFormat = change.mediaFormat;
        break;
      }

      case DatabaseChangeType.SET_FIELD_SETTINGS_MINIMUM_OR_MAXIMUM_FRACTION_DIGITS_FIELD:
      case DatabaseChangeType.SET_FIELD_SETTINGS_SEPARATOR_FIELD:
      case DatabaseChangeType.SET_FIELD_SETTINGS_PREFIX_OR_SUFFIX_FIELD: {
        const field = getDatabaseFieldOptional(database, change.fieldId);
        if (field) {
          Object.assign(field.settings, {
            ...field.settings,
            numberFormat: {
              ...field.settings.numberFormat,
              [change.changeType]: change.newValue,
            },
          });
        }
        break;
      }

      case DatabaseChangeType.SET_FIELD_SETTINGS_THOUSANDS_AND_DECIMAL_FIELD: {
        const field = getDatabaseFieldOptional(database, change.fieldId);
        if (field) {
          Object.assign(field.settings, {
            ...field.settings,
            numberFormat: {
              ...field.settings.numberFormat,
              thousandsSeparator: change.newValue.thousandsSeparator,
              decimalSeparator: change.newValue.decimalSeparator,
            },
          });
        }
        break;
      }

      case DatabaseChangeType.SET_FIELD_NAME: {
        const field = getDatabaseFieldOptional(database, change.fieldId);
        if (field) {
          field.name = change.name;
        }
        break;
      }

      case DatabaseChangeType.RESET_FIELD_NAME:
        // Currently we only support this in addChangeToChangeset()
        break;

      case DatabaseChangeType.SET_FIELD_DESCRIPTION: {
        const field = getDatabaseFieldOptional(database, change.fieldId);
        if (field) {
          field.description = change.description;
        }
        break;
      }

      case DatabaseChangeType.SET_FIELD_TYPE: {
        const field = getDatabaseFieldOptional(database, change.fieldId);
        if (field) {
          field.type = change.fieldType;
        }
        break;
      }

      case DatabaseChangeType.SET_FIELD_WIDTH: {
        const field = getDatabaseFieldOptional(database, change.fieldId);
        if (field) {
          field.width = change.width;
        }
        break;
      }

      case DatabaseChangeType.REORDER_FIELD: {
        const newIndex = getNewIndexWithHiddenFields(
          change.newIndex,
          database.fields,
          change.hiddenFields
        );

        database.fields = [
          ...database.fields
            .slice(0, newIndex)
            .filter((fieldId) => fieldId !== change.fieldId),
          change.fieldId,
          ...database.fields
            .slice(newIndex)
            .filter((fieldId) => fieldId !== change.fieldId),
        ];
        break;
      }

      case DatabaseChangeType.CLONE_FIELDS: {
        // Sort field IDs by their occurrence in the database and filter out non-existing ones
        const fieldIds = database.fields.filter((fieldId) =>
          Object.prototype.hasOwnProperty.call(change.fieldIds, fieldId)
        );

        if (fieldIds.length > 0) {
          const fieldNames = getExistingFieldNames(database);
          const newFieldNames: string[] = [];
          const newFields = fieldIds.map((fieldId) => {
            const field = getDatabaseFieldRequired(database, fieldId);
            const newFieldName = getUniqueFieldName(field.name, [
              ...fieldNames,
              ...newFieldNames,
            ]);
            newFieldNames.push(newFieldName);
            const newFieldId = change.fieldIds[fieldId];
            const newField = createField(field.type, {
              ...field,
              id: newFieldId,
              name: newFieldName,
            });
            database.views.forEach((view) => {
              if (view.settings.fields) {
                view.settings.fields[newFieldId] = view.id === change.currentViewId;
              }
            });
            return newField;
          });

          Object.assign(
            database.fieldsById,
            Object.fromEntries(newFields.map((f) => [f.id, f]))
          );

          const index = database.fields.indexOf(fieldIds[fieldIds.length - 1]) + 1;
          const fieldIdsToInsert = newFields.map((f) => f.id);
          for (let i = 0; i < fieldIdsToInsert.length; i++) {
            database.fields.splice(index + i, 0, fieldIdsToInsert[i]);
          }

          for (const entry of [database.defaultEntry, ...getDatabaseEntries(database)]) {
            for (const fieldId of fieldIds) {
              entry.values[change.fieldIds[fieldId]] = cloneDeep(entry.values[fieldId]);
            }
          }
        }

        break;
      }

      case DatabaseChangeType.DELETE_FIELD:
        database.fields = database.fields.filter((fieldId) => fieldId !== change.fieldId);

        delete database.fieldsById[change.fieldId];

        database.views.forEach((view) => {
          if (view.settings.fields) {
            delete view.settings.fields[change.fieldId];
          }
        });
        break;

      case DatabaseChangeType.UNDELETE_FIELD:
        // Currently we only support this in addChangeToChangeset()
        break;

      case DatabaseChangeType.SET_SHOW_FIELD_DESCRIPTION: {
        database.fieldsById[change.fieldId].showDescription = change.showDescription;
        break;
      }

      case DatabaseChangeType.SET_VALUE: {
        if (change.entryId === DEFAULT_ENTRY_ID) {
          database.defaultEntry.values[change.fieldId] = change.value;
        } else if (database.entriesById[change.entryId]) {
          database.entriesById[change.entryId].values[change.fieldId] = change.value;
        }
        break;
      }

      case DatabaseChangeType.CREATE_FIELD_AND_SET_VALUE: {
        // add field
        database.fieldsById[change.field.id] = change.field;
        if (change.index !== undefined) {
          database.fields.splice(change.index, 0, change.field.id);
        } else {
          database.fields.push(change.field.id);
        }

        database.views.forEach((view) => {
          if (view.settings.fields && change.field) {
            view.settings.fields[change.field.id] = view.id === change.currentViewId;
          }
        });

        // set value
        if (change.entryId === DEFAULT_ENTRY_ID) {
          database.defaultEntry.values[change.field.id] = change.value;
        } else if (database.entriesById[change.entryId]) {
          database.entriesById[change.entryId].values[change.field.id] = change.value;
        }
        break;
      }

      case DatabaseChangeType.BULK_SET_UNDEFINED_VALUES: {
        for (const entryId of database.entries) {
          const entry = getDatabaseEntryRequired(database, entryId);
          if (!Object.prototype.hasOwnProperty.call(entry.values, change.fieldId)) {
            entry.values[change.fieldId] = clone(change.value);
          }
        }
        break;
      }

      case DatabaseChangeType.RESET_VALUE:
        // Currently we only support this in addChangeToChangeset()
        break;

      case DatabaseChangeType.REORDER_ENTRIES: {
        const newIdx = Math.min(database.entries.length, change.newIndex);
        database.entries = [
          ...database.entries
            .slice(0, newIdx)
            .filter((entryId) => !change.entryIds.includes(entryId)),
          ...change.entryIds,
          ...database.entries
            .slice(newIdx)
            .filter((entryId) => !change.entryIds.includes(entryId)),
        ];
        break;
      }

      case DatabaseChangeType.CLONE_ENTRIES: {
        // Sort entry IDs by their occurrence in the database and filter out non-existing ones
        const entryIds = database.entries.filter((entryId) =>
          Object.prototype.hasOwnProperty.call(change.entryIds, entryId)
        );

        if (entryIds.length > 0) {
          const newEntries = entryIds.map((entryId) =>
            createEntry(database, undefined, {
              id: change.entryIds[entryId],
              values: cloneDeep(getDatabaseEntryRequired(database, entryId).values),
            })
          );

          Object.assign(
            database.entriesById,
            Object.fromEntries(newEntries.map((e) => [e.id, e]))
          );

          const index = database.entries.indexOf(entryIds[entryIds.length - 1]) + 1;
          const entryIdsToInsert = newEntries.map((e) => e.id);
          for (let i = 0; i < entryIdsToInsert.length; i++) {
            database.entries.splice(index + i, 0, entryIdsToInsert[i]);
          }
        }

        break;
      }

      case DatabaseChangeType.DELETE_ENTRY: {
        database.entries = database.entries.filter(
          (entryId) => entryId !== change.entryId
        );
        delete database.entriesById[change.entryId];
        break;
      }

      case DatabaseChangeType.UNDELETE_ENTRY:
        // Currently we only support this in addChangeToChangeset()
        break;

      case DatabaseChangeType.ADD_SAVED_VIEW:
        database.views.push(change.view);
        break;

      case DatabaseChangeType.SET_SAVED_VIEW: {
        const viewIdx = database.views.findIndex((view) => view.id === change.view.id);
        database.views[viewIdx] = change.view;
        break;
      }

      case DatabaseChangeType.DELETE_SAVED_VIEW:
        database.views = database.views.filter((view) => view.id !== change.viewId);
        break;

      case DatabaseChangeType.REORDER_SAVED_VIEW: {
        const view = getViewById(database.views, change.viewId);
        if (view) {
          const newIdx = Math.min(database.views.length, change.newIndex);
          database.views = [
            ...database.views
              .slice(0, newIdx)
              .filter((view) => view.id !== change.viewId),
            view,
            ...database.views.slice(newIdx).filter((view) => view.id !== change.viewId),
          ];
        }
        break;
      }

      case DatabaseChangeType.SET_DEFAULT_SAVED_VIEW:
        database.defaultViewId = change.viewId;
        break;

      case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_TYPE: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.LAYOUT_TYPE,
            value: change.newLayoutType,
          });
        }
        return database;
      }

      case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_CARD_WIDTH: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.LAYOUT_CARD_WIDTH,
            value: change.newCardWidth,
          });
        }
        return database;
      }

      case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_COVER_FIELD_ID: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.LAYOUT_COVER_FIELD_ID,
            value: change.newCoverFieldId,
          });
        }
        return database;
      }

      case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_FIELD_NAMES: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.LAYOUT_SHOW_FIELD_NAMES,
            value: change.newShowFieldNames,
          });
        }
        return database;
      }

      case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_GROUP_FIELD_ID: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.LAYOUT_GROUP_FIELD_ID,
            value: change.newGroupFieldId,
          });
        }
        return database;
      }

      case DatabaseChangeType.SET_GROUPING_FIELD: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_GROUPING_FIELD,
            fieldId: change.fieldId,
            isSubgroup: change.isSubgroup,
            manualGroupOrder: change.manualGroupOrder,
          });
        }
        return database;
      }

      case DatabaseChangeType.SET_GROUPING_SORT_TYPE: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_GROUPING_SORT_TYPE,
            sortType: change.sortType,
            isSubgroup: change.isSubgroup,
          });
        }
        return database;
      }

      case DatabaseChangeType.SET_GROUPING_MANUAL_GROUP_ORDER: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_GROUPING_MANUAL_GROUP_ORDER,
            manualGroupOrder: change.manualGroupOrder,
            isSubgroup: change.isSubgroup,
          });
        }
        return database;
      }

      case DatabaseChangeType.CLEAR_GROUPING: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_GROUPING_CLEAR,
            isSubgroup: change.isSubgroup,
          });
        }
        return database;
      }

      case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_DATE_FIELD_ID: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.LAYOUT_DATE_FIELD_ID,
            value: change.newDateFieldId,
          });
        }
        return database;
      }

      case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_NUMBERED_ENTRIES: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.LAYOUT_SHOW_NUMBERED_ENTRIES,
            value: change.newShowNumberedEntries,
          });
        }
        return database;
      }

      case DatabaseChangeType.UPDATE_SAVED_VIEW_NAME: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_NAME,
            value: change.newName,
            delta: change.delta,
          });
        }
        return database;
      }

      case DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_CALCULATIONS_SET,
            fieldKey: change.fieldId,
            value: change.value,
          });
        }
        return database;
      }

      case DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_CALCULATIONS_REMOVE,
            fieldKey: change.fieldId,
          });
        }
        return database;
      }

      case DatabaseChangeType.UPDATE_SAVED_VIEW_FIELDS: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_FIELDS,
            fieldKey: change.fieldKey,
            fieldValue: change.fieldValue,
            fields: change.existingFields,
          });
        }
        return database;
      }

      case DatabaseChangeType.UPDATE_SAVED_VIEW_SORT: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_SORT,
            sort: change.sort,
          });
        }
        return database;
      }

      case DatabaseChangeType.UPDATE_SAVED_VIEW_SORT_VALUE: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_SORT_UPDATE_VALUE,
            fieldId: change.fieldId,
            ascending: change.ascending,
            customOrder: change.customOrder,
          });
        }
        return database;
      }

      case DatabaseChangeType.INSERT_SAVED_VIEW_SORT: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_SORT_INSERT,
            sort: change.sort,
            index: change.index,
          });
        }
        return database;
      }

      case DatabaseChangeType.APPEND_SAVED_VIEW_SORT: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_SORT_APPEND,
            sort: change.sort,
          });
        }
        return database;
      }

      case DatabaseChangeType.DELETE_SAVED_VIEW_SORT: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_SORT_DELETE,
            fieldId: change.fieldId,
          });
        }
        return database;
      }

      case DatabaseChangeType.REORDER_SAVED_VIEW_SORT: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_SORT_REORDER,
            fieldId: change.fieldId,
            newIndex: change.newIndex,
          });
        }
        return database;
      }

      case DatabaseChangeType.CLEAR_SAVED_VIEW_SORT: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_SORT_CLEAR,
          });
        }
        return database;
      }

      case DatabaseChangeType.SET_SAVED_VIEW_FILTER_USAGE_TYPE: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_USAGE_TYPE,
            usageType: change.usageType,
            usageFilterId: change.usageFilterId,
            path: change.path,
          });
        }
        return database;
      }

      case DatabaseChangeType.SET_SAVED_VIEW_FILTER_VALUE: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewFilterChangeType.VIEW_FILTERS_SET_VALUE,
            filter: change.newFilter,
            path: change.path,
            change: change.change,
          });
        }
        return database;
      }

      case DatabaseChangeType.SET_SAVED_VIEW_FILTER:
      case DatabaseChangeType.SET_SAVED_VIEW_FILTER_FIELD_ID:
      case DatabaseChangeType.SET_SAVED_VIEW_FILTER_TYPE: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_FILTERS_SET,
            filter: change.newFilter,
            path: change.path,
          });
        }
        return database;
      }

      case DatabaseChangeType.APPEND_SAVED_VIEW_FILTER: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_FILTERS_APPEND,
            filterGroupId: change.filterGroupId,
            filterGroupPath: change.filterGroupPath,
            filter: change.newFilter,
            path: change.path,
          });
        }
        return database;
      }

      case DatabaseChangeType.REMOVE_SAVED_VIEW_FILTER: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_FILTERS_REMOVE,
            filterId: change.filterId,
            path: change.path,
          });
        }
        return database;
      }

      case DatabaseChangeType.CLEAR_SAVED_VIEW_FILTER: {
        const viewIdx = database.views.findIndex((view) => view.id === change.viewId);
        if (viewIdx >= 0) {
          database.views[viewIdx] = applyViewChange(database.views[viewIdx], {
            type: ViewChangeTypes.VIEW_FILTERS_CLEAR,
          });
        }
        return database;
      }

      case DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES: {
        change.changes.forEach((viewChange) => {
          applyDatabaseChange(database, viewChange);
        });
        return database;
      }

      case DatabaseChangeType.TOGGLE_LOCK_STRUCTURE: {
        throw new Error('Change type not supported.');
      }

      case DatabaseChangeType.CLEAR_ENTRIES:
        for (const entryId of change.entryIds || database.entries) {
          const entry = getDatabaseEntryRequired(database, entryId);
          for (const fieldId of change.fieldIds || database.fields) {
            const field = database.fieldsById[fieldId];
            if (!Object.prototype.hasOwnProperty.call(entry.values, fieldId)) {
              entry.values[fieldId] = getInitialValue(field.type);
            }
          }
        }
        break;

      case DatabaseChangeType.SET_DEFAULT_ENABLED: {
        database.fieldsById[change.fieldId].settings.defaultEnabled =
          change.defaultEnabled;
        break;
      }

      case DatabaseChangeType.SET_MENTION_NOTIFICATION_ENABLED: {
        database.fieldsById[change.fieldId].settings.mentionNotificationEnabled =
          change.mentionNotificationEnabled;
        break;
      }

      default:
        throw new Error(`Unknown change type ${(change as DatabaseChange).type}.`);
    }
  });
}

/**
 * Applies a changeset to the given database in series and returns the updated database. The changes are not persisted,
 * an updated copy of the database is simply returned. The original database object is not touched.
 */
export function applyDatabaseChangeset<
  Mode extends ObjectMode.EXISTING | ObjectMode.UPDATE
>(database: Database<Mode>, changeset: DatabaseChangeset): Database<Mode> {
  let result = database;
  for (const change of changeset) {
    result = applyDatabaseChange(result, change);
  }
  return result;
}

/**
 * Adds one or more database changes to the existing database changeset. The existing changeset is not modified, but a copy of it with the
 * new changes appended is returned. Obsolete changes are removed from the changeset, for example when a database value is updated multiple
 * times, only the latest update is preserved.
 */
export function addChangeToChangeset(
  changeset: DatabaseChange[],
  ...newChanges: DatabaseChange[]
): DatabaseChange[] {
  let result = changeset;
  for (const change of newChanges) {
    switch (change.type) {
      case DatabaseChangeType.RESET_FIELD:
        result = result.filter(
          (u) =>
            !(
              ((u.type === DatabaseChangeType.SET_FIELD_SETTINGS ||
                u.type === DatabaseChangeType.SET_FIELD_NAME ||
                u.type === DatabaseChangeType.SET_FIELD_DESCRIPTION ||
                u.type === DatabaseChangeType.SET_FIELD_TYPE) &&
                u.fieldId === change.fieldId) ||
              (u.type === DatabaseChangeType.UPDATE_FIELD &&
                u.field.id === change.fieldId)
            )
        );
        break;

      case DatabaseChangeType.RESET_FIELD_NAME:
        result = result.filter((u) => !(u.type === DatabaseChangeType.SET_FIELD_NAME));
        break;

      case DatabaseChangeType.UNDELETE_FIELD:
        result = result.filter(
          (u) =>
            !(u.type === DatabaseChangeType.DELETE_FIELD && u.fieldId === change.fieldId)
        );
        break;

      case DatabaseChangeType.BULK_SET_UNDEFINED_VALUES:
        result = [
          ...result.filter(
            (u) =>
              !(
                (u.type === DatabaseChangeType.SET_VALUE &&
                  u.fieldId === change.fieldId &&
                  u.entryId !== DEFAULT_ENTRY_ID) ||
                (u.type === DatabaseChangeType.BULK_SET_UNDEFINED_VALUES &&
                  u.fieldId === change.fieldId)
              )
          ),
          change,
        ];
        break;

      case DatabaseChangeType.RESET_VALUE:
        result = result.filter(
          (u) =>
            !(
              u.type === DatabaseChangeType.SET_VALUE &&
              u.fieldId === change.fieldId &&
              u.entryId === change.entryId
            )
        );
        break;

      case DatabaseChangeType.UNDELETE_ENTRY:
        result = result.filter(
          (u) =>
            !(u.type === DatabaseChangeType.DELETE_ENTRY && u.entryId === change.entryId)
        );
        break;

      default:
        result = [...result, change];
    }
  }
  return result;
}

/**
 * A database draft is a database with a changeset applied, with fields and entries deleted by the changeset still present
 * but marked as deleted in the "deletedFields" and "deletedEntries" properties.
 */
export type DatabaseDraft<Mode extends ObjectMode = ObjectMode.EXISTING> =
  Database<Mode> & {
    /** A list of field IDs that have been deleted. */
    deletedFields: string[];
    /** A list of entry IDs that have been deleted. */
    deletedEntries: string[];
  };

export function getDatabaseDraft<Mode extends ObjectMode.EXISTING | ObjectMode.UPDATE>(
  database: Database<Mode>,
  changeset: DatabaseChangeset
): DatabaseDraft<Mode> {
  const newDatabase = applyDatabaseChangeset(database, changeset);
  const newDatabaseWithoutDeletions = applyDatabaseChangeset(
    database,
    changeset.filter((change) => !DELETION_TYPES.includes(change.type))
  );
  return {
    ...newDatabaseWithoutDeletions,
    deletedFields: newDatabaseWithoutDeletions.fields.filter(
      (fieldId) => !newDatabase.fields.includes(fieldId)
    ),
    deletedEntries: newDatabaseWithoutDeletions.entries.filter(
      (entryId) => !newDatabase.entries.includes(entryId)
    ),
  };
}
